import { Add, Inventory2Outlined, Remove } from "@mui/icons-material";
import { Box, ButtonBase, IconButton, Typography } from "@mui/material";
import { useMemo } from "react";
import PropTypes from "prop-types";
import {
  formatCurrency,
  getStockRestante,
  isProductEligibleForVoucher,
} from "../utils";

export default function ProductGrid({
  products,
  cart,
  category,
  search,
  voucher,
  voucherCoveredUnits,
  voucherCoverageByProduct,
  onAdd,
  onQuantityChange,
}) {
  const isSpeciesVoucher = ["PRODUCTO", "CATEGORIA"].includes(voucher?.type);
  const visibleProducts = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return products
      .filter((product) => {
        const matchesCategory = category === "Todos" || product.category === category;
        const matchesSearch = !normalizedSearch || product.name.toLowerCase().includes(normalizedSearch);
        return matchesCategory && matchesSearch;
      })
      .sort((a, b) => Number(isProductEligibleForVoucher(b, voucher)) - Number(isProductEligibleForVoucher(a, voucher)));
  }, [category, products, search, voucher]);

  return (
    <Box>
      <Box
        data-testid="product-grid"
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(3, minmax(0, 1fr))",
            lg: "repeat(3, minmax(0, 1fr))",
            xxl: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        {visibleProducts.map((product) => {
          const quantity = cart[product.id] || 0;
          const stockRestante = getStockRestante(product.stockTeorico, quantity);
          const stockConocido = stockRestante !== null;
          const sinStockDisponible = !stockConocido || stockRestante <= 0;
          const agotadoInicial = stockConocido && Number(product.stockTeorico) <= 0;
          const voucherEligible = isProductEligibleForVoucher(product, voucher);
          const coveredQuantity = Number(voucherCoverageByProduct?.[product.id] || 0);
          const coverageAvailable = voucherCoveredUnits < Number(voucher?.units || 0);
          const coverageLabel = isSpeciesVoucher
            ? voucherEligible
              ? coveredQuantity
                ? `${coveredQuantity} CUBIERTA${coveredQuantity === 1 ? "" : "S"}`
                : !quantity && coverageAvailable
                  ? "RECOMENDADO"
                  : null
              : "SE COBRA APARTE"
            : null;
          return (
            <Box
              key={product.id}
              sx={{
                position: "relative",
                minHeight: { xs: 142, sm: 164 },
                borderRadius: "16px",
                border: sinStockDisponible
                  ? `2px solid ${agotadoInicial ? "#D86A62" : "#E29A2D"}`
                  : quantity
                    ? "2px solid #2C3087"
                    : voucherEligible && isSpeciesVoucher
                      ? "1px solid #88CDBD"
                      : "1px solid #E2E8F0",
                bgcolor: sinStockDisponible
                  ? agotadoInicial ? "#FFF3F2" : "#FFF8EB"
                  : quantity
                    ? "#F3F2FF"
                    : voucherEligible && isSpeciesVoucher
                      ? "#F5FCFA"
                      : "#fff",
                overflow: "hidden",
                textAlign: "left",
                transition: "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease",
                boxShadow: quantity
                  ? "0 10px 24px rgba(44, 48, 135, 0.13)"
                  : "0 3px 12px rgba(15, 23, 42, 0.06)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 24px rgba(15, 23, 42, 0.11)",
                  borderColor: "#2C3087",
                },
              }}
            >
              <ButtonBase
                data-testid={`product-${product.id}`}
                aria-label={`Agregar ${product.name}`}
                onClick={() => onAdd(product.id)}
                disabled={sinStockDisponible}
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 1,
                  borderRadius: "14px",
                  "&:focus-visible": { outline: "3px solid rgba(44, 48, 135, 0.28)", outlineOffset: -3 },
                }}
              />
              <Box
                sx={{
                  position: "relative",
                  zIndex: 0,
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  minHeight: "inherit",
                  p: 1.75,
                  pointerEvents: "none",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", pr: 10.5 }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: "14px",
                      display: "grid",
                      placeItems: "center",
                      fontSize: 29,
                      bgcolor: `${product.accent}18`,
                    }}
                  >
                    {product.emoji}
                  </Box>
                </Box>
                <Typography sx={{ mt: 1.25, fontWeight: 800, fontSize: 15, lineHeight: 1.2, color: "#172033" }}>
                  {product.name}
                </Typography>
                <Box
                  data-testid={`product-stock-${product.id}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.45,
                    width: "fit-content",
                    mt: 0.65,
                    px: 0.75,
                    py: 0.35,
                    borderRadius: "8px",
                    bgcolor: agotadoInicial ? "#FDECEA" : sinStockDisponible ? "#FFF0D5" : "#EEF8F4",
                    color: agotadoInicial ? "#B3261E" : sinStockDisponible ? "#80520D" : "#18735F",
                  }}
                >
                  <Inventory2Outlined sx={{ fontSize: 13.5, flexShrink: 0 }} />
                  <Typography component="span" sx={{ fontSize: 9.5, lineHeight: 1.2, fontWeight: 900 }}>
                    {!stockConocido
                      ? "Consultando stock..."
                      : agotadoInicial
                        ? "Sin stock: 0"
                        : stockRestante === 0
                          ? "Stock: 0 · límite"
                          : `Stock disponible: ${stockRestante}`}
                  </Typography>
                </Box>
                {coverageLabel && (
                  <Typography
                    component="span"
                    sx={{
                      width: "fit-content",
                      mt: 0.6,
                      px: 0.75,
                      py: 0.25,
                      borderRadius: "999px",
                      bgcolor: voucherEligible ? "#E0F5EF" : "#FFF4E5",
                      color: voucherEligible ? "#18735F" : "#9A6209",
                      fontSize: 9.5,
                      lineHeight: 1.2,
                      fontWeight: 900,
                    }}
                  >
                    {coverageLabel}
                  </Typography>
                )}
                <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", mt: "auto", pt: 1 }}>
                  <Typography sx={{ fontWeight: 800, color: product.accent, fontSize: 16 }}>
                    {formatCurrency(product.price)}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  zIndex: 2,
                  top: 14,
                  right: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.35,
                  p: 0.35,
                  border: "1px solid #DCE3EC",
                  borderRadius: "999px",
                  bgcolor: "#fff",
                  boxShadow: "0 3px 10px rgba(15, 23, 42, .08)",
                }}
              >
                <IconButton
                  size="small"
                  aria-label={`Quitar una unidad de ${product.name}`}
                  data-testid={`product-remove-${product.id}`}
                  disabled={quantity === 0}
                  onClick={() => onQuantityChange(product.id, -1)}
                  sx={{
                    width: 27,
                    height: 27,
                    color: "#475569",
                    bgcolor: "#F8FAFC",
                    "&:hover": { bgcolor: "#EEF2F7" },
                    "&.Mui-disabled": { color: "#CBD5E1", bgcolor: "#F8FAFC" },
                  }}
                >
                  <Remove sx={{ fontSize: 16 }} />
                </IconButton>
                <Typography
                  data-testid={`product-quantity-${product.id}`}
                  sx={{ minWidth: 20, textAlign: "center", fontSize: 12, fontWeight: 900, color: "#2C3087" }}
                >
                  {quantity}
                </Typography>
                <IconButton
                  size="small"
                  aria-label={`Agregar una unidad de ${product.name}`}
                  data-testid={`product-add-${product.id}`}
                  onClick={() => onQuantityChange(product.id, 1)}
                  disabled={sinStockDisponible}
                  sx={{
                    width: 27,
                    height: 27,
                    bgcolor: "#2C3087",
                    color: "#fff",
                    "&:hover": { bgcolor: "#22266F" },
                    "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
                  }}
                >
                  <Add sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          );
        })}
      </Box>

      {visibleProducts.length === 0 && (
        <Box sx={{ py: 7, textAlign: "center", color: "#64748B" }}>
          <Typography fontWeight={700}>No encontramos productos con ese nombre.</Typography>
        </Box>
      )}
    </Box>
  );
}

ProductGrid.propTypes = {
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  cart: PropTypes.object.isRequired,
  category: PropTypes.string.isRequired,
  search: PropTypes.string.isRequired,
  voucher: PropTypes.object,
  voucherCoveredUnits: PropTypes.number.isRequired,
  voucherCoverageByProduct: PropTypes.object.isRequired,
  onAdd: PropTypes.func.isRequired,
  onQuantityChange: PropTypes.func.isRequired,
};
